import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API, useTheme } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  User, GraduationCap, LayoutDashboard, Upload, CheckCircle2,
  ArrowLeft, ArrowRight, Save, Loader2, FileText, X, Check, Sparkles,
  AlertCircle, AlertTriangle, ChevronDown, Send, Clock, Mail, Sun, Moon, Star, Lock, Shield, Phone, Eye
} from "lucide-react";
import AIChat from "@/components/AIChat";
import PersonalInfoSections from "@/components/PersonalInfoSections";
import AcademicHistorySections from "@/components/AcademicHistorySections";
import EmploymentHistorySections from "@/components/EmploymentHistorySections";
import RecommendationSection from "@/components/RecommendationSection";
import ObservationHoursSection from "@/components/ObservationHoursSection";
import OnboardingModal from "@/components/OnboardingModal";
import StepOnboardingModal from "@/components/StepOnboardingModal";

// Transcript Request Row Component (extracted for proper hook usage)
const TranscriptRequestRow = ({ 
  institution, 
  index, 
  theme, 
  transcriptRequests, 
  requestingTranscript, 
  handleTranscriptRequest,
  idPrefix = "transcript",
  actionLabel = "I've Requested This"
}) => {
  // Support both "name" and "institution_name" field names
  const institutionName = institution.name || institution.institution_name || "";
  const baseId = institutionName.replace(/\s+/g, '_').toLowerCase() || `inst_${index}`;
  const institutionId = institution.request_id || baseId;
  const requestData = transcriptRequests[institutionId];
  const isRequested = requestData?.status === "requested";
  const [requestedDate, setRequestedDate] = useState(requestData?.requested_date || "");
  const [showDateInput, setShowDateInput] = useState(false);
  
  // Get degree info - support both old and new field names
  const degreeType = institution.degree || institution.degree_type || "";
  const major = institution.major || "";

  const handleMarkRequested = () => {
    if (requestedDate) {
      handleTranscriptRequest(institutionId, requestedDate);
      setShowDateInput(false);
    }
  };
  
  return (
    <div
      className={`backdrop-blur-xl border rounded-2xl p-5 transition-all duration-500 ${
        isRequested
          ? 'border-[#739600]/30 bg-[#739600]/5'
          : (theme === 'dark' ? 'bg-white/[0.02] border-white/[0.05] hover:border-[#00677F]/30' : 'bg-white border-gray-200 hover:border-[#00677F]/30')
      }`}
      data-testid={`${idPrefix}-row-${index}`}
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        {/* Institution Name */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isRequested ? 'bg-[#739600]/20' : 'bg-[#7B68EE]/10'}`}>
              {isRequested ? (
                <Check className="w-5 h-5 text-[#739600]" />
              ) : (
                <GraduationCap className="w-5 h-5 text-[#7B68EE]" />
              )}
            </div>
            <div className="min-w-0">
              <p className={`font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {institutionName || "Unknown Institution"}
              </p>
              <p className={`text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}>
                {degreeType} {major ? `in ${major}` : ""}
              </p>
            </div>
          </div>
        </div>
        
        {/* Date Input or Action Button */}
        <div className="flex items-center gap-3">
          {isRequested ? (
            <>
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#739600]/20 text-[#739600] text-sm font-medium">
                <Check className="w-4 h-4" />
                Requested
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#739600]" />
                <span className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                  {requestData?.requested_date ? new Date(requestData.requested_date).toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric'
                  }) : ""}
                </span>
              </div>
            </>
          ) : showDateInput ? (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Select
                  value={requestedDate ? requestedDate.split('-')[1] : ''}
                  onValueChange={(val) => {
                    const parts = requestedDate ? requestedDate.split('-') : [new Date().getFullYear().toString(), '', '01'];
                    setRequestedDate(`${parts[0]}-${val}-${parts[2] || '01'}`);
                  }}
                >
                  <SelectTrigger className={`h-10 w-[120px] rounded-lg border text-sm ${
                    theme === 'dark' ? 'bg-black/30 border-white/[0.08] text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                  }`} data-testid={`${idPrefix}-month-${index}`}>
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f1419] border-white/10 text-white max-h-60">
                    {[
                      { v: "01", l: "January" }, { v: "02", l: "February" }, { v: "03", l: "March" },
                      { v: "04", l: "April" }, { v: "05", l: "May" }, { v: "06", l: "June" },
                      { v: "07", l: "July" }, { v: "08", l: "August" }, { v: "09", l: "September" },
                      { v: "10", l: "October" }, { v: "11", l: "November" }, { v: "12", l: "December" }
                    ].map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select
                  value={requestedDate ? requestedDate.split('-')[2] : ''}
                  onValueChange={(val) => {
                    const parts = requestedDate ? requestedDate.split('-') : [new Date().getFullYear().toString(), '01', ''];
                    setRequestedDate(`${parts[0]}-${parts[1] || '01'}-${val}`);
                  }}
                >
                  <SelectTrigger className={`h-10 w-[80px] rounded-lg border text-sm ${
                    theme === 'dark' ? 'bg-black/30 border-white/[0.08] text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                  }`} data-testid={`${idPrefix}-day-${index}`}>
                    <SelectValue placeholder="Day" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f1419] border-white/10 text-white max-h-60">
                    {Array.from({ length: (() => {
                      const parts = requestedDate ? requestedDate.split('-') : [];
                      const m = parseInt(parts[1]); const y = parseInt(parts[0]);
                      return (m && y) ? new Date(y, m, 0).getDate() : 31;
                    })() }, (_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={requestedDate ? requestedDate.split('-')[0] : ''}
                  onValueChange={(val) => {
                    const parts = requestedDate ? requestedDate.split('-') : ['', '01', '01'];
                    setRequestedDate(`${val}-${parts[1] || '01'}-${parts[2] || '01'}`);
                  }}
                >
                  <SelectTrigger className={`h-10 w-[90px] rounded-lg border text-sm ${
                    theme === 'dark' ? 'bg-black/30 border-white/[0.08] text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                  }`} data-testid={`${idPrefix}-year-${index}`}>
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f1419] border-white/10 text-white max-h-60">
                    {[2026, 2025, 2024, 2023, 2022, 2021, 2020].map(year => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleMarkRequested}
                disabled={!requestedDate || requestingTranscript[institutionId]}
                className="rounded-full bg-[#739600] hover:bg-[#5a7400] text-white px-6"
                data-testid={`${idPrefix}-confirm-${index}`}
              >
                {requestingTranscript[institutionId] ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Confirm"
                )}
              </Button>
              <button
                onClick={() => setShowDateInput(false)}
                className={`text-sm ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Cancel
              </button>
            </div>
          ) : (
            <Button
              onClick={() => setShowDateInput(true)}
              className="rounded-full bg-[#00677F] hover:bg-[#135163] text-white px-6"
              data-testid={`${idPrefix}-request-${index}`}
            >
              {actionLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const ApplicationForm = () => {
  const navigate = useNavigate();
  const { appId, step: urlStep } = useParams();
  const { token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(parseInt(urlStep) || 1);
  const [showChat, setShowChat] = useState(false);
  const [programs, setPrograms] = useState(null);
  const [expandedTranscript, setExpandedTranscript] = useState(false);
  const [transcriptRequests, setTranscriptRequests] = useState({});
  const [requestingTranscript, setRequestingTranscript] = useState({});
  const [credentialRequests, setCredentialRequests] = useState({});
  const [requestingCredential, setRequestingCredential] = useState({});
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showStepOnboarding, setShowStepOnboarding] = useState(false);
  const [showBgCheckBlocker, setShowBgCheckBlocker] = useState(false);
  const [bgCheckDeclined, setBgCheckDeclined] = useState(false);
  const [ageRestricted, setAgeRestricted] = useState(false);
  const [showAdvisorContact, setShowAdvisorContact] = useState(false);

  // Form state
  const [personalInfo, setPersonalInfo] = useState({});
  const [academicHistory, setAcademicHistory] = useState({});
  const [programSelection, setProgramSelection] = useState({});
  const [financialAid, setFinancialAid] = useState({});
  const [employmentHistory, setEmploymentHistory] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const [observationHours, setObservationHours] = useState([]);
  
  // Submitted state — locks all form fields
  const isSubmitted = application?.status === "submitted";

  // Programs that show the Recommendation step
  const RECOMMENDATION_PROGRAMS = ["Nursing", "Education", "Certificates"];
  const showRecommendationStep = RECOMMENDATION_PROGRAMS.includes(programSelection.program_type);

  // Employment History stage only shows for OTA Entry pathways
  const OTA_ENTRY_PATHWAYS = [
    "Master of Occupational Therapy (OTA Entry)",
    "Flex Master of Occupational Therapy (OTA Entry)",
    "Hybrid Immersion Master of Occupational Therapy (OTA Entry)",
  ];
  const showEmploymentHistoryStep = OTA_ENTRY_PATHWAYS.includes(programSelection.program_pathway);

  // Observation Hours shows for OT programs EXCEPT OTA Entry pathways
  const showObservationHoursStep = programSelection.program_type === "Occupational Therapy" && !OTA_ENTRY_PATHWAYS.includes(programSelection.program_pathway);

  // Build steps dynamically with correct numbering
  const buildSteps = () => {
    const s = [
      { step: 1, label: "Personal Information", icon: User },
      { step: 2, label: "Academic History", icon: GraduationCap },
    ];
    let nextStep = 3;
    if (showEmploymentHistoryStep) {
      s.push({ step: nextStep, label: "Employment History", icon: LayoutDashboard });
      nextStep++;
    }
    s.push({ step: nextStep, label: "Documents Required", icon: Upload });
    nextStep++;
    s.push({ step: nextStep, label: "Official Documents", icon: Send });
    nextStep++;
    if (showObservationHoursStep) {
      s.push({ step: nextStep, label: "Observation Hours", icon: Eye });
      nextStep++;
    }
    if (showRecommendationStep) {
      s.push({ step: nextStep, label: "Recommendation", icon: Star });
      nextStep++;
    }
    s.push({ step: nextStep, label: "Review & Submit", icon: CheckCircle2 });
    return s;
  };

  const steps = buildSteps();
  const reviewStep = steps[steps.length - 1].step;

  useEffect(() => {
    fetchApplication();
    fetchPrograms();
  }, [appId]);

  useEffect(() => {
    if (urlStep) {
      const stepNum = parseInt(urlStep);
      // Guard: if step doesn't exist in steps array, go to step 1
      const validStep = steps.find(s => s.step === stepNum);
      setCurrentStep(validStep ? stepNum : 1);
    }
  }, [urlStep]);

  // Show onboarding for first-time users on step 1 (general intro)
  useEffect(() => {
    if (!loading && currentStep === 1 && appId) {
      // Store onboarding completion per application, not globally
      const onboardingKey = `onboarding_completed_${appId}`;
      const hasSeenOnboarding = localStorage.getItem(onboardingKey);
      if (!hasSeenOnboarding) {
        // Small delay to let the page render first
        setTimeout(() => {
          setShowOnboarding(true);
        }, 500);
      } else {
        // If they've seen the general onboarding, check for step-specific
        const stepOnboardingKey = `step_onboarding_${appId}_1`;
        const hasSeenStepOnboarding = localStorage.getItem(stepOnboardingKey);
        if (!hasSeenStepOnboarding) {
          setTimeout(() => {
            setShowStepOnboarding(true);
          }, 300);
        }
      }
    }
  }, [loading, currentStep, appId]);

  // Show step-specific onboarding when entering a new step (steps 2+)
  useEffect(() => {
    if (!loading && appId && currentStep > 1) {
      const label = steps.find(s => s.step === currentStep)?.label || currentStep;
      const stepOnboardingKey = `step_onboarding_${appId}_${label}`;
      const hasSeenStepOnboarding = localStorage.getItem(stepOnboardingKey);
      if (!hasSeenStepOnboarding) {
        setTimeout(() => {
          setShowStepOnboarding(true);
        }, 300);
      }
    }
  }, [loading, currentStep, appId]);

  const fetchApplication = async () => {
    try {
      const response = await axios.get(`${API}/applications/${appId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplication(response.data);
      setPersonalInfo(response.data.personal_info || {});
      setAcademicHistory(response.data.academic_history || {});
      setProgramSelection(response.data.program_selection || {});
      setFinancialAid(response.data.financial_aid || {});
      setEmploymentHistory(response.data.employment_history || {});
      setRecommendations(response.data.recommendations || []);
      setObservationHours(response.data.observation_hours || []);
      setTranscriptRequests(response.data.transcript_requests || {});
      setCredentialRequests(response.data.credential_requests || {});
      // Check if user previously declined background check
      if ((response.data.personal_info || {}).judicial_agreement === "disagree") {
        setBgCheckDeclined(true);
      }
      // Check if user was age-restricted
      if ((response.data.personal_info || {}).age_restricted === true) {
        setAgeRestricted(true);
      }
      if (!urlStep) {
        setCurrentStep(response.data.current_step || 1);
      }
    } catch (error) {
      console.error("Error fetching application:", error);
      toast.error("Failed to load application");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API}/programs`);
      setPrograms(response.data);
    } catch (error) {
      console.error("Error fetching programs:", error);
    }
  };

  const saveProgress = async (nextStep = null) => {
    if (isSubmitted) return false; // Cannot modify submitted application
    setSaving(true);
    try {
      const updateData = {
        personal_info: personalInfo,
        academic_history: academicHistory,
        program_selection: programSelection,
        financial_aid: financialAid,
        employment_history: employmentHistory,
        recommendations: recommendations,
        observation_hours: observationHours,
        current_step: nextStep || currentStep
      };

      const response = await axios.put(
        `${API}/applications/${appId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApplication(response.data);
      toast.success("Progress saved");
      return true;
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Failed to save progress");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const currentIdx = steps.findIndex(s => s.step === currentStep);
    if (currentIdx < steps.length - 1) {
      const nextStepNum = steps[currentIdx + 1].step;
      const saved = await saveProgress(nextStepNum);
      if (saved) {
        setCurrentStep(nextStepNum);
        navigate(`/application/${appId}/${nextStepNum}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleStepChange = async (targetStep) => {
    const saved = await saveProgress(targetStep);
    if (saved) {
      setCurrentStep(targetStep);
      navigate(`/application/${appId}/${targetStep}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    const currentIdx = steps.findIndex(s => s.step === currentStep);
    if (currentIdx > 0) {
      const prevStepNum = steps[currentIdx - 1].step;
      setCurrentStep(prevStepNum);
      navigate(`/application/${appId}/${prevStepNum}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    // Block submission if background check not agreed
    if (personalInfo.judicial_agreement !== "agree") {
      setShowBgCheckBlocker(true);
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/applications/${appId}/submit`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Application submitted successfully!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Error submitting:", error);
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (docId, file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(
        `${API}/applications/${appId}/documents/${docId}/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      toast.success("Document uploaded successfully");
      fetchApplication();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload document");
    }
  };

  const handleMultipleFileUpload = async (docId, files) => {
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        
        await axios.post(
          `${API}/applications/${appId}/documents/${docId}/upload-transcript`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data"
            }
          }
        );
      }
      toast.success(`${files.length} transcript${files.length > 1 ? 's' : ''} uploaded successfully`);
      fetchApplication();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload transcripts");
    }
  };

  const handleRemoveTranscriptFile = async (docId, fileIndex) => {
    try {
      await axios.delete(
        `${API}/applications/${appId}/documents/${docId}/transcript/${fileIndex}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success("Transcript removed");
      fetchApplication();
    } catch (error) {
      console.error("Remove error:", error);
      toast.error("Failed to remove transcript");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E14] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#00677F] animate-spin" />
      </div>
    );
  }

  // Helper: get the step label for the current step number
  const getCurrentStepLabel = () => {
    const found = steps.find(s => s.step === currentStep);
    return found ? found.label : "";
  };

  // Helper: get the next step number after the current one
  const getNextStepNumber = () => {
    const idx = steps.findIndex(s => s.step === currentStep);
    return idx < steps.length - 1 ? steps[idx + 1].step : currentStep;
  };

  const renderStepContent = () => {
    const stepLabel = getCurrentStepLabel();

    switch (stepLabel) {
      case "Personal Information":
        return (
          <PersonalInfoSections 
            personalInfo={personalInfo} 
            setPersonalInfo={setPersonalInfo}
            programSelection={programSelection}
            onSectionComplete={() => handleStepChange(getNextStepNumber())}
            isSubmitted={isSubmitted}
            onBgCheckDeclined={async () => {
              setBgCheckDeclined(true);
              // Persist the decline to backend immediately
              try {
                await axios.put(`${API}/applications/${appId}`, 
                  { personal_info: { ...personalInfo, judicial_agreement: "disagree" } },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
              } catch (_) {}
            }}
            onAgeRestriction={async () => {
              setAgeRestricted(true);
              try {
                await axios.put(`${API}/applications/${appId}`, 
                  { personal_info: { ...personalInfo, age_restricted: true } },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
              } catch (_) {}
            }}
          />
        );

      case "Academic History":
        return (
          <AcademicHistorySections 
            academicHistory={academicHistory} 
            setAcademicHistory={setAcademicHistory}
            onSectionComplete={() => handleStepChange(getNextStepNumber())}
            isSubmitted={isSubmitted}
            programSelection={application?.program_selection}
          />
        );

      case "Employment History":
        return (
          <EmploymentHistorySections 
            employmentHistory={employmentHistory} 
            setEmploymentHistory={setEmploymentHistory}
            onSectionComplete={() => handleStepChange(getNextStepNumber())}
            isSubmitted={isSubmitted}
            appId={appId}
            token={token}
          />
        );

      case "Documents Required":
        return (
          <div className={`space-y-8 ${isSubmitted ? 'pointer-events-none opacity-70' : ''}`} data-testid="step-4-documents">
            {/* Section Header */}
            <div className="relative">
              <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#9C27B0] to-[#E91E63] rounded-full" />
              <h3 className="text-2xl font-light text-white tracking-tight">Documents Required</h3>
              <p className="text-slate-500 text-sm mt-1">Upload all required documents for your application</p>
            </div>

            {/* Requirements Notice - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 hover:border-[#FF6B35]/20 transition-colors duration-500">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#FF6B35]/10 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-[#FF6B35]" />
                </div>
                <div className="space-y-2">
                  <p className="text-white font-medium">Document Requirements</p>
                  <p className="text-slate-400 text-sm">
                    Upload clear, legible copies of all required documents. Accepted formats: PDF, DOC, DOCX, JPG, PNG
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {["PDF", "DOC", "DOCX", "JPG", "PNG"].map(format => (
                      <span key={format} className="px-2 py-1 rounded-full bg-white/5 text-slate-500 text-xs">
                        {format}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Upload Progress Summary */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 hover:border-[#00677F]/20 transition-colors duration-500">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#00677F]/10 flex items-center justify-center">
                    <Upload className="w-4 h-4 text-[#00677F]" />
                  </div>
                  <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Upload Progress</span>
                </div>
                <span className="text-white font-medium">
                  {application?.documents?.filter(d => d.type !== "recommendation" && d.status === "uploaded").length || 0} / {application?.documents?.filter(d => d.type !== "recommendation").length || 4} uploaded
                </span>
              </div>
              <div className="h-2 bg-black/30 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#00677F] to-[#A1D8E0] rounded-full transition-all duration-500"
                  style={{ width: `${((application?.documents?.filter(d => d.type !== "recommendation" && d.status === "uploaded").length || 0) / (application?.documents?.filter(d => d.type !== "recommendation").length || 4)) * 100}%` }}
                />
              </div>
            </div>

            {/* Credential Evaluation Notice */}
            {application?.documents?.some(d => d.type === "credential_evaluation") && (
              <div className="backdrop-blur-xl bg-[#7B68EE]/5 border border-[#7B68EE]/20 rounded-2xl p-5 transition-colors duration-500" data-testid="credential-eval-notice">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#7B68EE]/10 flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-5 h-5 text-[#7B68EE]" />
                  </div>
                  <div className="space-y-1">
                    <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>International Institution Detected</p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                      A <strong>Credential Evaluation Form</strong> has been added to your required documents because one or more of your academic institutions is located outside the United States. This evaluation must be completed by an approved credential evaluation agency.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Documents List */}
            <div className="space-y-4">
              {application?.documents?.filter(d => d.type !== "recommendation").map((doc, index) => (
                <div
                  key={doc.id}
                  className={`backdrop-blur-xl bg-white/[0.02] border rounded-2xl p-5 transition-all duration-500 ${
                    doc.status === "uploaded"
                      ? "border-[#739600]/30 hover:border-[#739600]/50"
                      : "border-white/[0.05] hover:border-[#00677F]/30"
                  }`}
                  data-testid={`document-${doc.type}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all ${
                        doc.status === "uploaded" 
                          ? "bg-[#739600]/20" 
                          : "bg-white/5"
                      }`}>
                        {doc.status === "uploaded" ? (
                          <Check className="w-7 h-7 text-[#739600]" />
                        ) : (
                          <FileText className="w-7 h-7 text-slate-500" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          {doc.type === "transcript" && doc.files && doc.files.length > 0 ? (
                            <button
                              onClick={() => setExpandedTranscript(!expandedTranscript)}
                              className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
                            >
                              <p className="text-white font-medium">{doc.name}</p>
                              <span className="text-slate-400">•</span>
                              <span className="text-[#00677F] font-medium">
                                Total: {doc.files.length}
                              </span>
                              <ChevronDown className={`w-4 h-4 text-[#00677F] transition-transform duration-300 ${expandedTranscript ? 'rotate-180' : ''}`} />
                            </button>
                          ) : (
                            <p className="text-white font-medium">{doc.name}</p>
                          )}
                          {doc.status !== "uploaded" && (
                            <span className="px-2 py-0.5 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] text-xs">Required</span>
                          )}
                        </div>
                        <p className="text-slate-500 text-sm mt-1">
                          {doc.type === "transcript" ? (
                            doc.files && doc.files.length > 0 
                              ? "Click to view and manage transcripts"
                              : "Upload transcripts from all schools attended"
                          ) : (
                            doc.status === "uploaded"
                              ? `Uploaded ${new Date(doc.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                              : "No file uploaded yet"
                          )}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {doc.type === "transcript" && (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            multiple
                            onChange={(e) => {
                              if (e.target.files?.length > 0) {
                                handleMultipleFileUpload(doc.id, Array.from(e.target.files));
                              }
                            }}
                            data-testid={`upload-${doc.type}`}
                          />
                          <span className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                            doc.status === "uploaded"
                              ? "bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10"
                              : "bg-[#00677F] hover:bg-[#135163] text-white shadow-[0_0_15px_rgba(0,180,216,0.3)]"
                          }`}>
                            <Upload className="w-4 h-4" />
                            {doc.status === "uploaded" ? "Add More" : "Upload"}
                          </span>
                        </label>
                      )}
                      {doc.type !== "transcript" && (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                handleFileUpload(doc.id, e.target.files[0]);
                              }
                            }}
                            data-testid={`upload-${doc.type}`}
                          />
                          <span className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                            doc.status === "uploaded"
                              ? "bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10"
                              : "bg-[#00677F] hover:bg-[#135163] text-white shadow-[0_0_15px_rgba(0,180,216,0.3)]"
                          }`}>
                            <Upload className="w-4 h-4" />
                            {doc.status === "uploaded" ? "Replace" : "Upload"}
                          </span>
                        </label>
                      )}
                    </div>
                  </div>
                  
                  {/* Expandable transcript files list */}
                  {doc.type === "transcript" && doc.files && doc.files.length > 0 && expandedTranscript && (
                    <div className="mt-4 pt-4 border-t border-white/[0.05] space-y-2 animate-in slide-in-from-top-2 duration-300">
                      <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-3">Uploaded Transcripts ({doc.files.length})</p>
                      {doc.files.map((file, fileIndex) => (
                        <div key={fileIndex} className="flex items-center justify-between py-3 px-4 rounded-xl bg-white/[0.02] border border-white/[0.03] hover:border-white/[0.08] transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-[#739600]/10 flex items-center justify-center">
                              <FileText className="w-4 h-4 text-[#739600]" />
                            </div>
                            <div>
                              <span className="text-white text-sm font-medium">{file.name}</span>
                              <p className="text-slate-500 text-xs">
                                Uploaded {new Date(file.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveTranscriptFile(doc.id, fileIndex)}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors text-sm"
                            data-testid={`remove-transcript-${fileIndex}`}
                          >
                            <X className="w-3.5 h-3.5" />
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Disclosure Notice - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 hover:border-[#7B68EE]/20 transition-colors duration-500">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#7B68EE]/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-[#7B68EE]" />
                </div>
                <div className="space-y-3">
                  <p className="text-white font-medium">Important Disclosure</p>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    By submitting these documents, you certify that all information provided is true and accurate to the best of your knowledge. 
                    Falsification of any document may result in rejection of your application or dismissal from the program.
                  </p>
                  <p className="text-slate-500 text-xs">
                    All documents are encrypted and stored securely in compliance with FERPA regulations.
                  </p>
                </div>
              </div>
            </div>

            {/* Completion Status */}
            {application?.documents?.every(d => d.status === "uploaded") && (
              <div className="flex items-center justify-center p-6 rounded-xl bg-gradient-to-r from-[#739600]/10 to-[#00677F]/10 border border-[#739600]/20 animate-in fade-in duration-500">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-[#739600]/20 flex items-center justify-center mx-auto mb-3">
                    <Check className="w-6 h-6 text-[#739600]" />
                  </div>
                  <p className="text-white font-medium">All Documents Uploaded!</p>
                  <p className="text-slate-500 text-sm mt-1">You're ready to proceed to the final review step</p>
                </div>
              </div>
            )}
          </div>
        );

      case "Official Documents":
        // Request Official Transcripts Step
        const institutions = academicHistory.institutions || academicHistory.previous_institutions || [];
        
        // Transcript exchange services configuration
        const transcriptServices = [
          { id: "parchment", name: "Parchment" },
          { id: "nsc", name: "National Student Clearinghouse" },
          { id: "portage", name: "Portage" },
          { id: "cas", name: "CAS" },
          { id: "jst", name: "Joint Service Transcripts" },
          { id: "certified_mail", name: "USA Certified Mail" }
        ];
        
        // Mock mapping of institutions to supported services (in production, this would come from backend)
        const getInstitutionServices = (instName) => {
          // For demo purposes, randomly assign some services. In production, this comes from backend config
          const name = (instName || "").toLowerCase();
          if (name.includes("california") || name.includes("ucla")) return ["parchment", "nsc"];
          if (name.includes("stanford")) return ["nsc", "cas"];
          if (name.includes("michigan")) return ["parchment", "portage"];
          if (name.includes("harvard") || name.includes("mit")) return ["nsc", "cas"];
          if (name.includes("military") || name.includes("army") || name.includes("navy")) return ["jst"];
          // Default: most institutions support at least one service
          return ["parchment", "nsc"];
        };
        
        const handleTranscriptRequest = async (institutionId, requestedDate) => {
          setRequestingTranscript(prev => ({ ...prev, [institutionId]: true }));
          
          try {
            const response = await axios.post(
              `${API}/applications/${appId}/transcript-request`,
              {
                institution_id: institutionId,
                requested_date: requestedDate,
                timestamp: new Date().toISOString()
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setTranscriptRequests(prev => ({
              ...prev,
              [institutionId]: {
                requested_date: requestedDate,
                status: "requested",
                timestamp: new Date().toISOString()
              }
            }));
            
            toast.success("Transcript request confirmed!");
          } catch (error) {
            console.error("Transcript request error:", error);
            toast.error("Failed to save transcript request. Please try again.");
          } finally {
            setRequestingTranscript(prev => ({ ...prev, [institutionId]: false }));
          }
        };

        // ===== Credential Evaluation (international institutions) =====
        const US_TERRITORIES = [
          "Puerto Rico", "Guam", "U.S. Virgin Islands", "US Virgin Islands",
          "American Samoa", "Northern Mariana Islands"
        ];
        const isInternationalInstitution = (inst) => {
          const c = inst.country || inst.institution_country || "";
          return !!c && c !== "United States" && !US_TERRITORIES.includes(c);
        };
        const internationalInstitutions = institutions.filter(isInternationalInstitution);
        // First Professional = all Occupational Therapy programs/pathways; Post Professional = all others
        const isFirstProfessional = programSelection.program_type === "Occupational Therapy";
        const requiresCredentialEvaluation = internationalInstitutions.length > 0;

        // First Professional -> one eval per international institution.
        // Post Professional -> a single combined eval regardless of count.
        const credentialItems = isFirstProfessional
          ? internationalInstitutions.map((inst, i) => {
              const nm = inst.name || inst.institution_name || `International Institution ${i + 1}`;
              return {
                ...inst,
                name: nm,
                request_id: `cred_${(nm).replace(/\s+/g, '_').toLowerCase()}_${i}`,
              };
            })
          : (requiresCredentialEvaluation ? [{
              name: "International Credential Evaluation",
              degree: `${internationalInstitutions.length} international institution${internationalInstitutions.length > 1 ? "s" : ""}`,
              request_id: "cred_combined",
            }] : []);

        const handleCredentialRequest = async (institutionId, requestedDate) => {
          setRequestingCredential(prev => ({ ...prev, [institutionId]: true }));
          try {
            await axios.post(
              `${API}/applications/${appId}/credential-request`,
              { institution_id: institutionId, requested_date: requestedDate, timestamp: new Date().toISOString() },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setCredentialRequests(prev => ({
              ...prev,
              [institutionId]: { requested_date: requestedDate, status: "requested", timestamp: new Date().toISOString() }
            }));
            toast.success("Credential evaluation request confirmed!");
          } catch (error) {
            console.error("Credential request error:", error);
            toast.error("Failed to save credential evaluation request. Please try again.");
          } finally {
            setRequestingCredential(prev => ({ ...prev, [institutionId]: false }));
          }
        };

        return (
          <div className="space-y-8" data-testid="step-5-transcripts">
            {/* Section Header */}
            <div className="relative">
              <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#00677F] to-[#7B68EE] rounded-full" />
              <h3 className={`text-2xl font-light tracking-tight ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Request Official Transcripts</h3>
              <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-slate-500' : 'text-gray-600'}`}>Official transcripts must be sent directly from each institution to the University of St. Augustine</p>
            </div>

            {/* Important Distinction Notice */}
            <div className={`backdrop-blur-xl border rounded-2xl p-6 transition-colors duration-500 ${theme === 'dark' ? 'bg-[#DC5034]/5 border-[#DC5034]/20' : 'bg-red-50 border-red-200'}`} data-testid="transcript-official-notice">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#DC5034]/10 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-[#DC5034]" />
                </div>
                <div className="space-y-3">
                  <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Official vs. Unofficial Transcripts</p>
                  <div className={`text-sm space-y-2 ${theme === 'dark' ? 'text-slate-300' : 'text-gray-700'}`}>
                    <p>
                      <strong className={`${theme === 'dark' ? 'text-[#739600]' : 'text-green-700'}`}>Official Transcripts</strong> — Must be sent <strong>directly from the institution</strong> (registrar's office) to the University of St. Augustine. 
                      These can be sent electronically through an approved transcript exchange service or via certified mail.
                    </p>
                    <p>
                      <strong className={`${theme === 'dark' ? 'text-[#DC5034]' : 'text-red-700'}`}>Unofficial Transcripts</strong> — Any transcript sent, delivered, or uploaded <strong>by the applicant</strong> is considered unofficial 
                      and <strong>will not satisfy</strong> this requirement, even if it bears an institutional seal.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* How To Request Info */}
            <div className={`backdrop-blur-xl border rounded-2xl p-6 transition-colors duration-500 ${theme === 'dark' ? 'bg-white/[0.02] border-white/[0.05] hover:border-[#00677F]/20' : 'bg-white border-gray-200 hover:border-[#00677F]/30'}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#00677F]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#00677F]" />
                </div>
                <div className="space-y-2">
                  <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>How to Request</p>
                  <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                    Contact each institution's registrar office and request they send your official transcripts directly to the University of St. Augustine. 
                    Once you've submitted the request, confirm it below with the date requested.
                  </p>
                </div>
              </div>
            </div>

            {/* Institutions Table */}
            {institutions.length === 0 ? (
              <div className={`backdrop-blur-xl border rounded-2xl p-10 text-center ${theme === 'dark' ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-white border-gray-200'}`}>
                <div className="w-16 h-16 rounded-full bg-[#FF9800]/10 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-[#FF9800]" />
                </div>
                <h4 className={`font-medium mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>No Institutions Found</h4>
                <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                  Please go back to Step 2 (Academic History) and add your previous institutions before requesting transcripts.
                </p>
                <Button
                  onClick={() => { setCurrentStep(2); navigate(`/application/${appId}/2`); }}
                  className="mt-4 bg-[#00677F] hover:bg-[#135163] text-white rounded-full px-6"
                >
                  Go to Academic History
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {institutions.map((inst, index) => (
                  <TranscriptRequestRow
                    key={inst.institution_name?.replace(/\s+/g, '_').toLowerCase() || `inst_${index}`}
                    institution={inst}
                    index={index}
                    theme={theme}
                    transcriptRequests={transcriptRequests}
                    requestingTranscript={requestingTranscript}
                    handleTranscriptRequest={handleTranscriptRequest}
                  />
                ))}
              </div>
            )}

            {/* Progress Summary */}
            {institutions.length > 0 && (
              <div className={`backdrop-blur-xl border rounded-2xl p-6 ${theme === 'dark' ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#00677F]/10 flex items-center justify-center">
                      <Send className="w-5 h-5 text-[#00677F]" />
                    </div>
                    <div>
                      <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Transcript Requests</p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-slate-500' : 'text-gray-600'}`}>
                        {Object.keys(transcriptRequests).length} of {institutions.length} institutions requested
                      </p>
                    </div>
                  </div>
                  <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                    Object.keys(transcriptRequests).length === institutions.length
                      ? "bg-[#739600]/20 text-[#739600]"
                      : "bg-[#FF9800]/20 text-[#FF9800]"
                  }`}>
                    {Object.keys(transcriptRequests).length === institutions.length ? "All Requested" : "In Progress"}
                  </div>
                </div>
              </div>
            )}

            {/* Mailing Address for Physical Transcripts */}
            <div className={`backdrop-blur-xl border rounded-2xl p-6 transition-colors duration-500 ${theme === 'dark' ? 'bg-white/[0.02] border-white/[0.05] hover:border-[#FF9800]/20' : 'bg-white border-gray-200 hover:border-[#FF9800]/30'}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#FF9800]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#FF9800]" />
                </div>
                <div className="space-y-3">
                  <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Send Transcripts by Mail</p>
                  <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                    If sending official transcripts by mail, please have them sent to:
                  </p>
                  <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-black/20 border border-white/[0.05]' : 'bg-gray-50 border border-gray-200'}`}>
                    <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Admissions Office</p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-gray-700'}`}>University of St. Augustine for Health Sciences</p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-gray-700'}`}>1 News Place</p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-gray-700'}`}>St. Augustine, FL 32086</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== Credential Evaluation Section ===== */}
            {requiresCredentialEvaluation && (
              <div className="space-y-6 pt-4" data-testid="credential-evaluation-section">
                {/* Divider header */}
                <div className="relative">
                  <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#7B68EE] to-[#00677F] rounded-full" />
                  <h3 className={`text-2xl font-light tracking-tight ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Request Credential Evaluation</h3>
                  <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-slate-500' : 'text-gray-600'}`}>
                    Required for academic institutions located outside the United States or U.S. territories
                  </p>
                </div>

                {/* Disclosure Notice */}
                <div className={`backdrop-blur-xl border rounded-2xl p-6 transition-colors duration-500 ${theme === 'dark' ? 'bg-[#7B68EE]/5 border-[#7B68EE]/20' : 'bg-indigo-50 border-indigo-200'}`} data-testid="credential-eval-disclosure">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#7B68EE]/10 flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-5 h-5 text-[#7B68EE]" />
                    </div>
                    <div className="space-y-3">
                      <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Why a Credential Evaluation is Required</p>
                      <div className={`text-sm space-y-2 ${theme === 'dark' ? 'text-slate-300' : 'text-gray-700'}`}>
                        <p>
                          Because one or more of your academic institutions is located outside the United States, you must obtain a
                          <strong> credential evaluation</strong> from an approved evaluation agency (e.g., WES, ECE, or another NACES member).
                          The evaluation must be sent <strong>directly from the agency</strong> to the University of St. Augustine.
                        </p>
                        <p className={`${theme === 'dark' ? 'text-[#A1D8E0]' : 'text-[#00677F]'}`}>
                          {isFirstProfessional
                            ? "For your program, a separate credential evaluation is required for each international institution listed."
                            : "For your program, only one credential evaluation is required regardless of how many international institutions you listed."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* How To Request */}
                <div className={`backdrop-blur-xl border rounded-2xl p-6 transition-colors duration-500 ${theme === 'dark' ? 'bg-white/[0.02] border-white/[0.05] hover:border-[#7B68EE]/20' : 'bg-white border-gray-200 hover:border-[#7B68EE]/30'}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#7B68EE]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[#7B68EE]" />
                    </div>
                    <div className="space-y-2">
                      <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>How to Request</p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                        Contact an approved credential evaluation agency and request a course-by-course evaluation be sent directly to the University of St. Augustine.
                        Once you've submitted the request, confirm it below with the date requested.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Credential Rows */}
                <div className="space-y-4">
                  {credentialItems.map((item, index) => (
                    <TranscriptRequestRow
                      key={item.request_id}
                      institution={item}
                      index={index}
                      theme={theme}
                      transcriptRequests={credentialRequests}
                      requestingTranscript={requestingCredential}
                      handleTranscriptRequest={handleCredentialRequest}
                      idPrefix="credential"
                    />
                  ))}
                </div>

                {/* Progress Summary */}
                <div className={`backdrop-blur-xl border rounded-2xl p-6 ${theme === 'dark' ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#7B68EE]/10 flex items-center justify-center">
                        <Send className="w-5 h-5 text-[#7B68EE]" />
                      </div>
                      <div>
                        <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Credential Evaluations</p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-slate-500' : 'text-gray-600'}`}>
                          {credentialItems.filter(i => credentialRequests[i.request_id]?.status === "requested").length} of {credentialItems.length} requested
                        </p>
                      </div>
                    </div>
                    <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                      credentialItems.every(i => credentialRequests[i.request_id]?.status === "requested")
                        ? "bg-[#739600]/20 text-[#739600]"
                        : "bg-[#FF9800]/20 text-[#FF9800]"
                    }`}>
                      {credentialItems.every(i => credentialRequests[i.request_id]?.status === "requested") ? "All Requested" : "In Progress"}
                    </div>
                  </div>
                </div>

                {/* Mailing Address for Credential Evaluation */}
                <div className={`backdrop-blur-xl border rounded-2xl p-6 transition-colors duration-500 ${theme === 'dark' ? 'bg-white/[0.02] border-white/[0.05] hover:border-[#FF9800]/20' : 'bg-white border-gray-200 hover:border-[#FF9800]/30'}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#FF9800]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[#FF9800]" />
                    </div>
                    <div className="space-y-3">
                      <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Send Credential Evaluation by Mail</p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                        If sending your official credential evaluation by mail, please have it sent to:
                      </p>
                      <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-black/20 border border-white/[0.05]' : 'bg-gray-50 border border-gray-200'}`}>
                        <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Admissions Office</p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-gray-700'}`}>University of St. Augustine for Health Sciences</p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-gray-700'}`}>1 News Place</p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-gray-700'}`}>St. Augustine, FL 32086</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "Recommendation":
        return (
          <RecommendationSection
            recommendations={recommendations}
            setRecommendations={setRecommendations}
            requiredCount={2}
            appId={appId}
          />
        );

      case "Observation Hours":
        return (
          <ObservationHoursSection
            observations={observationHours}
            setObservations={setObservationHours}
            appId={appId}
            token={token}
          />
        );

      case "Review & Submit":
        return (
          <div className="space-y-8" data-testid="step-5-review">
            {/* Section Header */}
            <div className="relative">
              <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-[#739600] to-[#00677F] rounded-full" />
              <h3 className="text-2xl font-light text-white tracking-tight">Review & Submit</h3>
              <p className="text-slate-500 text-sm mt-1">Review your application before final submission</p>
            </div>

            {/* Completion Status - Glass Card */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 hover:border-[#00677F]/20 transition-colors duration-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#00677F]/10 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-[#00677F]" />
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase tracking-wider">Application Progress</p>
                    <p className="text-2xl font-light text-white">{application?.progress || 0}% <span className="text-sm text-slate-500">Complete</span></p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-slate-500 text-xs uppercase tracking-wider">Status</p>
                  <span className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium ${
                    application?.status === "submitted" 
                      ? "bg-[#739600]/20 text-[#739600]" 
                      : "bg-[#FF9800]/20 text-[#FF9800]"
                  }`}>
                    {application?.status === "submitted" ? "Submitted" : "In Progress"}
                  </span>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-4 pt-4 border-t border-white/[0.05]">
                <div className="h-2 bg-black/30 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-[#00677F] to-[#739600] rounded-full transition-all duration-500"
                    style={{ width: `${application?.progress || 0}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Summary Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Info Card */}
              <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 hover:border-[#00677F]/20 transition-colors duration-500 group">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#00677F]/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-[#00677F]" />
                    </div>
                    <h4 className="text-white font-medium">Personal Information</h4>
                  </div>
                  <button 
                    onClick={() => { setCurrentStep(1); navigate(`/application/${appId}/1`); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/5 text-[#00677F] hover:bg-[#00677F]/10 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    Edit
                  </button>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-white/[0.03]">
                    <span className="text-slate-500 text-sm">Name</span>
                    <span className="text-white text-sm">{personalInfo.first_name} {personalInfo.last_name}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/[0.03]">
                    <span className="text-slate-500 text-sm">Email</span>
                    <span className="text-slate-300 text-sm">{personalInfo.email || "Not provided"}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/[0.03]">
                    <span className="text-slate-500 text-sm">Phone</span>
                    <span className="text-slate-300 text-sm">{personalInfo.phone || "Not provided"}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-slate-500 text-sm">Location</span>
                    <span className="text-slate-300 text-sm">
                      {personalInfo.city && personalInfo.state ? `${personalInfo.city}, ${personalInfo.state}` : "Not provided"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Academic History Card */}
              <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 hover:border-[#7B68EE]/20 transition-colors duration-500 group">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#7B68EE]/10 flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-[#7B68EE]" />
                    </div>
                    <h4 className="text-white font-medium">Academic History</h4>
                  </div>
                  <button 
                    onClick={() => { setCurrentStep(2); navigate(`/application/${appId}/2`); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/5 text-[#7B68EE] hover:bg-[#7B68EE]/10 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    Edit
                  </button>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-white/[0.03]">
                    <span className="text-slate-500 text-sm">Highest Degree</span>
                    <span className="text-white text-sm capitalize">{academicHistory.highest_degree?.replace('_', ' ') || "Not provided"}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/[0.03]">
                    <span className="text-slate-500 text-sm">Prior Application</span>
                    <span className="text-slate-300 text-sm capitalize">{academicHistory.prior_application || "Not specified"}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/[0.03]">
                    <span className="text-slate-500 text-sm">TOEFL</span>
                    <span className="text-slate-300 text-sm">{academicHistory.toefl_total ? `Score: ${academicHistory.toefl_total}` : "Not required"}</span>
                  </div>
                  {/* Institutions List */}
                  <div className="pt-2">
                    <span className="text-slate-500 text-xs uppercase tracking-wider">Institutions ({(academicHistory.institutions || []).length})</span>
                    <div className="mt-2 space-y-2">
                      {(academicHistory.institutions || []).length > 0 ? (
                        (academicHistory.institutions || []).map((inst, idx) => {
                          // Format date from YYYY-MM-DD to MM DD, YYYY
                          const formatDate = (dateStr) => {
                            if (!dateStr) return "Date not set";
                            const [year, month, day] = dateStr.split('-');
                            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                            return `${monthNames[parseInt(month) - 1] || ''} ${parseInt(day) || ''}, ${year}`.replace(' ,', ',');
                          };
                          return (
                            <div key={idx} className="flex items-center justify-between py-2 px-3 rounded-lg bg-black/20 border border-white/[0.03]">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-[#7B68EE]/10 flex items-center justify-center text-xs text-[#7B68EE]">{idx + 1}</div>
                                <span className="text-slate-300 text-sm">{inst.name || "Unknown"}</span>
                                {inst.country && inst.country !== "United States" && (
                                  <span className="text-slate-500 text-xs">({inst.country})</span>
                                )}
                              </div>
                              <div className="text-right">
                                <span className="text-slate-400 text-xs">{inst.degree || "Degree not set"}</span>
                                <span className="text-slate-500 text-xs ml-2">• {formatDate(inst.date)}</span>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-slate-600 text-xs italic">No institutions added</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Employment History Card */}
              <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 hover:border-[#FF9800]/20 transition-colors duration-500 group">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#FF9800]/10 flex items-center justify-center">
                      <LayoutDashboard className="w-5 h-5 text-[#FF9800]" />
                    </div>
                    <h4 className="text-white font-medium">Employment History</h4>
                  </div>
                  <button 
                    onClick={() => { setCurrentStep(3); navigate(`/application/${appId}/3`); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/5 text-[#FF9800] hover:bg-[#FF9800]/10 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    Edit
                  </button>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-white/[0.03]">
                    <span className="text-slate-500 text-sm">Verifications</span>
                    <span className="text-white text-sm">{(employmentHistory.verifications || []).length} submitted</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/[0.03]">
                    <span className="text-slate-500 text-sm">Confirmed</span>
                    <span className="text-slate-300 text-sm">
                      {(employmentHistory.verifications || []).filter(v => v.date_confirmed).length} of {(employmentHistory.verifications || []).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/[0.03]">
                    <span className="text-slate-500 text-sm">Total Hours</span>
                    <span className={`text-sm font-medium ${
                      (employmentHistory.verifications || []).reduce((sum, v) => sum + (parseInt(v.hours_worked) || 0), 0) >= 2080
                        ? "text-[#739600]"
                        : "text-[#FF6B35]"
                    }`}>
                      {(employmentHistory.verifications || []).reduce((sum, v) => sum + (parseInt(v.hours_worked) || 0), 0).toLocaleString()} / 2,080
                    </span>
                  </div>
                  {/* Employment Verifications List */}
                  <div className="pt-2">
                    <span className="text-slate-500 text-xs uppercase tracking-wider">Employment Records</span>
                    <div className="mt-2 space-y-2">
                      {(employmentHistory.verifications || []).length > 0 ? (
                        (employmentHistory.verifications || []).map((v, idx) => {
                          // Format date from YYYY-MM-DD to MM DD, YYYY
                          const formatDate = (dateStr) => {
                            if (!dateStr) return "Present";
                            const [year, month, day] = dateStr.split('-');
                            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                            return `${monthNames[parseInt(month) - 1] || ''} ${parseInt(day) || ''}, ${year}`.replace(' ,', ',');
                          };
                          return (
                            <div key={idx} className="py-2 px-3 rounded-lg bg-black/20 border border-white/[0.03]">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-slate-300 text-sm font-medium">{v.employer_name || "Unknown Employer"}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${v.date_confirmed ? 'bg-[#739600]/20 text-[#739600]' : 'bg-[#FF9800]/20 text-[#FF9800]'}`}>
                                  {v.date_confirmed ? "Verified" : "Pending"}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-slate-500">{v.job_title || "No title"}</span>
                                <span className="text-slate-500">{formatDate(v.start_date)} - {formatDate(v.end_date)} • {v.hours_worked || 0} hrs</span>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-slate-600 text-xs italic">No employment records added</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Documents Card */}
              <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 hover:border-[#E91E63]/20 transition-colors duration-500 group">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#E91E63]/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-[#E91E63]" />
                    </div>
                    <h4 className="text-white font-medium">Documents Required</h4>
                  </div>
                  <button 
                    onClick={() => { setCurrentStep(4); navigate(`/application/${appId}/4`); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/5 text-[#E91E63] hover:bg-[#E91E63]/10 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    Edit
                  </button>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-2 border-b border-white/[0.03]">
                    <span className="text-slate-500 text-sm">Uploaded</span>
                    <span className="text-white text-sm">
                      {application?.documents?.filter(d => d.status === "uploaded").length || 0} of {application?.documents?.length || 5}
                    </span>
                  </div>
                  <div className="space-y-2 pt-2">
                    {application?.documents?.map((doc) => (
                      <div key={doc.id} className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                          doc.status === "uploaded" ? "bg-[#739600]/20" : "bg-white/5"
                        }`}>
                          {doc.status === "uploaded" ? (
                            <Check className="w-3 h-3 text-[#739600]" />
                          ) : (
                            <X className="w-3 h-3 text-slate-500" />
                          )}
                        </div>
                        <span className={`text-sm ${doc.status === "uploaded" ? "text-slate-300" : "text-slate-500"}`}>
                          {doc.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Transcript Requests Summary */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 hover:border-[#9C27B0]/20 transition-colors duration-500">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#9C27B0]/10 flex items-center justify-center">
                    <Send className="w-5 h-5 text-[#9C27B0]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Transcript Requests</h4>
                    <p className="text-slate-500 text-xs">Official transcripts from your institutions</p>
                  </div>
                </div>
                <button 
                  onClick={() => { setCurrentStep(5); navigate(`/application/${appId}/5`); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/5 text-[#9C27B0] hover:bg-[#9C27B0]/10 transition-colors"
                >
                  Edit
                </button>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-white/[0.03]">
                  <span className="text-slate-500 text-sm">Requested</span>
                  <span className="text-white text-sm">
                    {Object.keys(transcriptRequests).length} of {(academicHistory.institutions || []).length} institutions
                  </span>
                </div>
                {/* Transcript Request Details */}
                <div className="pt-2 space-y-2">
                  {(academicHistory.institutions || []).length > 0 ? (
                    (academicHistory.institutions || []).map((inst, idx) => {
                      const instId = (inst.name || '').replace(/\s+/g, '_').toLowerCase() || `inst_${idx}`;
                      const request = transcriptRequests[instId];
                      const isRequested = request?.status === "requested";
                      // Format date from YYYY-MM-DD to Month Day, Year
                      const formatRequestDate = (dateStr) => {
                        if (!dateStr) return "";
                        const [year, month, day] = dateStr.split('-');
                        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                        return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
                      };
                      return (
                        <div key={idx} className="flex items-center justify-between py-2 px-3 rounded-lg bg-black/20 border border-white/[0.03]">
                          <div className="flex items-center gap-2">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isRequested ? 'bg-[#739600]/20' : 'bg-white/5'}`}>
                              {isRequested ? (
                                <Check className="w-3 h-3 text-[#739600]" />
                              ) : (
                                <Clock className="w-3 h-3 text-slate-500" />
                              )}
                            </div>
                            <span className="text-slate-300 text-sm">{inst.name || "Unknown"}</span>
                          </div>
                          <span className={`text-xs ${isRequested ? 'text-[#739600]' : 'text-slate-500'}`}>
                            {isRequested ? `Requested ${formatRequestDate(request.requested_date)}` : "Not requested"}
                          </span>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-slate-600 text-xs italic">No institutions added in Academic History</p>
                  )}
                </div>
              </div>
            </div>

            {/* Program Selection Summary */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 hover:border-[#00677F]/20 transition-colors duration-500">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[#00677F]/10 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-[#00677F]" />
                </div>
                <div>
                  <h4 className="text-white font-medium">Selected Program</h4>
                  <p className="text-slate-500 text-xs">Your chosen degree program</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 sm:p-4 rounded-xl bg-black/20 border border-white/[0.03]">
                  <p className="text-slate-600 text-xs uppercase tracking-wider mb-1">Program</p>
                  <p className="text-white font-medium">{programSelection.program_type || "Not selected"}</p>
                </div>
                <div className="p-4 rounded-xl bg-black/20 border border-white/[0.03]">
                  <p className="text-slate-600 text-xs uppercase tracking-wider mb-1">Pathway</p>
                  <p className="text-white font-medium">{programSelection.program_pathway || "Not selected"}</p>
                </div>
                <div className="p-4 rounded-xl bg-black/20 border border-white/[0.03]">
                  <p className="text-slate-600 text-xs uppercase tracking-wider mb-1">Campus</p>
                  <p className="text-white font-medium capitalize">{programSelection.campus || programSelection.primary_campus || "Not selected"}</p>
                </div>
                <div className="p-4 rounded-xl bg-black/20 border border-white/[0.03]">
                  <p className="text-slate-600 text-xs uppercase tracking-wider mb-1">Start Term</p>
                  <p className="text-white font-medium">{programSelection.start_term || "Not selected"}</p>
                </div>
              </div>
            </div>

            {/* Certification & Agreement */}
            <div className="backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 hover:border-[#7B68EE]/20 transition-colors duration-500">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#7B68EE]/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <AlertCircle className="w-5 h-5 text-[#7B68EE]" />
                </div>
                <div className="space-y-3">
                  <h4 className="text-white font-medium">Certification & Agreement</h4>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    By clicking "Submit Application," I certify that all information provided in this application is true, 
                    accurate, and complete to the best of my knowledge. I understand that any falsification, misrepresentation, 
                    or omission of information may result in denial of admission or dismissal from the program.
                  </p>
                  <p className="text-slate-500 text-xs">
                    I authorize the University of St. Augustine for Health Sciences to verify all information provided 
                    and to contact references as needed.
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Section */}
            <div className="backdrop-blur-xl bg-gradient-to-br from-[#00677F]/10 to-[#7B68EE]/10 border border-[#00677F]/20 rounded-2xl p-6 sm:p-10 text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00677F]/20 to-[#7B68EE]/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-[#00677F]" />
              </div>
              <h3 className="font-['Montserrat'] text-xl sm:text-3xl font-light text-white mb-3">
                Ready to Submit?
              </h3>
              <p className="text-slate-400 mb-8 max-w-lg mx-auto">
                Please review all information carefully. Once submitted, you will not be able to make changes to your application. 
                Our admissions team will review your application and contact you within 2-3 business days.
              </p>
              
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="bg-gradient-to-r from-[#00677F] to-[#7B68EE] hover:opacity-90 text-white rounded-full px-8 sm:px-14 py-5 sm:py-7 text-base sm:text-lg shadow-[0_0_40px_rgba(0,180,216,0.4)] hover:shadow-[0_0_60px_rgba(0,180,216,0.5)] transition-all duration-300"
                data-testid="submit-application-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                    Submitting Application...
                  </>
                ) : (
                  <>
                    <Check className="w-6 h-6 mr-3" />
                    Submit Application
                  </>
                )}
              </Button>
              
              <p className="text-slate-600 text-xs mt-6">
                By submitting, you agree to our Terms of Service and Privacy Policy
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#0A0E14]' : 'bg-gray-50'}`}>
      {/* Onboarding Modal for first-time users (Step 1 only) */}
      <OnboardingModal 
        isOpen={showOnboarding} 
        onComplete={() => setShowOnboarding(false)}
        appId={appId}
      />

      {/* Step-specific onboarding modal (Steps 2-6) */}
      <StepOnboardingModal
        isOpen={showStepOnboarding}
        step={currentStep}
        stepLabel={getCurrentStepLabel()}
        appId={appId}
        onComplete={() => setShowStepOnboarding(false)}
      />

      {/* Background Check Blocker Modal */}
      {showBgCheckBlocker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="bg-check-blocker-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl shadow-black/50 animate-in fade-in zoom-in-95 duration-200">
            <div className="relative px-6 py-5 bg-gradient-to-r from-[#DC5034] to-[#FF6B35]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-white text-lg font-semibold">Background Check Required</h4>
                  <p className="text-white/70 text-xs">Disclosure Notice</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0f1419] px-6 py-6 space-y-5">
              <p className="text-slate-200 text-sm leading-relaxed">
                In order to move forward with your application, you <strong className="text-white">must agree to the background check requirement</strong>. 
                This is a mandatory step in the admissions process for the University of St. Augustine for Health Sciences.
              </p>
              <div className="p-4 rounded-xl bg-[#FF6B35]/5 border border-[#FF6B35]/15">
                <p className="text-slate-300 text-sm leading-relaxed">
                  All applicants are required to complete a criminal background check as part of the admission process. 
                  Clinical affiliates may require additional background checks prior to clinical placement. 
                  Certain criminal convictions may affect eligibility for licensure in your chosen field.
                </p>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                If you choose not to consent to a background check, your application <strong className="text-slate-200">cannot be processed</strong> and 
                you will not be able to proceed to the next steps. Please contact your Enrollment Advisor if you have any questions or concerns.
              </p>
            </div>
            <div className="bg-[#0f1419] border-t border-white/[0.06] px-6 pb-6 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setBgCheckDeclined(true);
                  setShowBgCheckBlocker(false);
                }}
                className="border-white/20 text-slate-300 hover:bg-white/5 rounded-xl px-5"
                data-testid="bg-blocker-decline-btn"
              >
                I Still Do Not Agree
              </Button>
              <Button
                onClick={() => {
                  setPersonalInfo(prev => ({ ...prev, judicial_agreement: "agree" }));
                  setShowBgCheckBlocker(false);
                  setBgCheckDeclined(false);
                  toast.success("Background check agreement accepted. You may now submit.");
                }}
                className="bg-[#739600] hover:bg-[#5e7a00] text-white rounded-xl px-6 shadow-[0_0_15px_rgba(115,150,0,0.3)]"
                data-testid="bg-blocker-agree-btn"
              >
                I Agree to Background Check
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Background Check Declined Overlay - Blocks entire form */}
      {bgCheckDeclined && !showBgCheckBlocker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="bg-check-declined-overlay">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
          <div className="relative w-full max-w-md text-center space-y-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="w-16 h-16 rounded-full bg-[#DC5034]/15 border border-[#DC5034]/30 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-[#DC5034]" />
            </div>
            <div>
              <h3 className="text-white text-xl font-['Montserrat'] font-bold mb-2">Application Paused</h3>
              <p className="text-slate-400 text-sm leading-relaxed max-w-sm mx-auto">
                You must agree to the background check requirement to continue your application. 
                If you have questions, please contact your Enrollment Advisor.
              </p>
            </div>
            <div className="flex flex-col gap-3 items-center">
              <Button
                onClick={async () => {
                  setPersonalInfo(prev => ({ ...prev, judicial_agreement: "agree" }));
                  setBgCheckDeclined(false);
                  setShowAdvisorContact(false);
                  try {
                    await axios.put(`${API}/applications/${appId}`, 
                      { personal_info: { ...personalInfo, judicial_agreement: "agree" } },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                  } catch (_) {}
                  toast.success("Background check agreement accepted. You may continue.");
                }}
                className="bg-[#739600] hover:bg-[#5e7a00] text-white rounded-xl px-6 py-3 shadow-[0_0_15px_rgba(115,150,0,0.3)] w-full sm:w-auto"
                data-testid="declined-agree-btn"
              >
                I Agree to Background Check
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowAdvisorContact(!showAdvisorContact)}
                className="border-white/20 text-slate-300 hover:bg-white/5 rounded-xl px-6 py-3 w-full sm:w-auto"
                data-testid="declined-contact-advisor-btn"
              >
                {showAdvisorContact ? "Hide Advisor Info" : "Contact Enrollment Advisor"}
              </Button>
            </div>

            {/* Expanded Advisor Contact Info */}
            {showAdvisorContact && (
              <div className="mt-2 backdrop-blur-xl bg-white/[0.04] border border-white/[0.08] rounded-2xl p-5 text-left space-y-3 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="advisor-contact-info">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#00677F]/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#00677F]" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Dr. Sarah Mitchell</p>
                    <p className="text-slate-500 text-xs">Enrollment Advisor</p>
                  </div>
                </div>
                <div className="space-y-2 pl-[52px]">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#00677F]" />
                    <a href="tel:+19045551234" className="text-[#A1D8E0] text-sm hover:underline">(904) 555-1234</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#00677F]" />
                    <a href="mailto:admissions@usa.edu" className="text-[#A1D8E0] text-sm hover:underline">admissions@usa.edu</a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Age Restriction Overlay - Blocks entire form */}
      {ageRestricted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="age-restricted-overlay">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
          <div className="relative w-full max-w-md text-center space-y-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="w-16 h-16 rounded-full bg-[#FF9800]/15 border border-[#FF9800]/30 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-[#FF9800]" />
            </div>
            <div>
              <h3 className="text-white text-xl font-['Montserrat'] font-bold mb-2">Age Requirement Not Met</h3>
              <p className="text-slate-400 text-sm leading-relaxed max-w-sm mx-auto">
                Applicants must be at least 10 years of age to continue. 
                If you believe this is an error, please contact your Enrollment Advisor.
              </p>
            </div>
            <div className="flex flex-col gap-3 items-center">
              <Button
                onClick={async () => {
                  setPersonalInfo(prev => ({ ...prev, date_of_birth: "", age_restricted: false }));
                  setAgeRestricted(false);
                  setShowAdvisorContact(false);
                  try {
                    await axios.put(`${API}/applications/${appId}`, 
                      { personal_info: { ...personalInfo, date_of_birth: "", age_restricted: false } },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                  } catch (_) {}
                  toast.success("Date of birth cleared. Please re-enter your correct date of birth.");
                }}
                className="bg-[#739600] hover:bg-[#5e7a00] text-white rounded-xl px-6 py-3 shadow-[0_0_15px_rgba(115,150,0,0.3)] w-full sm:w-auto"
                data-testid="age-fix-dob-btn"
              >
                Correct My Date of Birth
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowAdvisorContact(!showAdvisorContact)}
                className="border-white/20 text-slate-300 hover:bg-white/5 rounded-xl px-6 py-3 w-full sm:w-auto"
                data-testid="age-contact-advisor-btn"
              >
                {showAdvisorContact ? "Hide Advisor Info" : "Contact Enrollment Advisor"}
              </Button>
            </div>

            {showAdvisorContact && (
              <div className="mt-2 backdrop-blur-xl bg-white/[0.04] border border-white/[0.08] rounded-2xl p-5 text-left space-y-3 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="age-advisor-contact-info">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#00677F]/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#00677F]" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Dr. Sarah Mitchell</p>
                    <p className="text-slate-500 text-xs">Enrollment Advisor</p>
                  </div>
                </div>
                <div className="space-y-2 pl-[52px]">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#00677F]" />
                    <a href="tel:+19045551234" className="text-[#A1D8E0] text-sm hover:underline">(904) 555-1234</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#00677F]" />
                    <a href="mailto:admissions@usa.edu" className="text-[#A1D8E0] text-sm hover:underline">admissions@usa.edu</a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}


      {/* Top Navigation */}
      <nav className={`sticky top-0 z-40 backdrop-blur-xl border-b ${theme === 'dark' ? 'bg-[#0A0E14]/80 border-white/5' : 'bg-white/80 border-gray-200'}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/dashboard")}
            className={`flex items-center gap-2 transition-colors ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Dashboard</span>
          </button>
          
          <div className="text-center">
            <p className={`font-['Montserrat'] font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Application</p>
            <p className={`text-sm ${theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}>{application?.program_selection?.program_type || "Program"}</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-colors ${theme === 'dark' ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
              data-testid="theme-toggle-btn"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            {/* Ask Journey Button */}
            <Button
              onClick={() => setShowChat(true)}
              className={`bg-gradient-to-r from-[#00677F]/20 to-[#7B68EE]/20 border border-[#00677F]/30 hover:border-[#A1D8E0] hover:bg-[#00677F]/30 rounded-full px-4 py-2 flex items-center gap-2 transition-all ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}
              data-testid="ask-journey-btn"
            >
              <Sparkles className="w-4 h-4 text-[#A1D8E0]" />
              <span className="hidden sm:inline">Ask Journey</span>
            </Button>
            
            {!isSubmitted && (
              <Button
                onClick={() => saveProgress()}
                disabled={saving}
                variant="ghost"
                className="text-[#00677F] hover:text-[#A1D8E0] hover:bg-[#00677F]/10"
                data-testid="save-progress-btn"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                <span className="hidden sm:inline ml-2">Save</span>
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Progress Bar */}
      <div className={`border-b ${theme === 'dark' ? 'bg-[#11161F] border-white/5' : 'bg-white border-gray-200'}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between mb-3">
            <span className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>Step {currentStep} of {steps.length}</span>
            <span className="text-[#00677F] text-sm font-medium">{application?.progress || 0}% Complete</span>
          </div>
          <Progress value={(currentStep / steps.length) * 100} className={`h-2 ${theme === 'dark' ? 'bg-slate-800' : 'bg-gray-200'}`} />
        </div>
      </div>

      {/* Step Navigation */}
      <div className={`border-b ${theme === 'dark' ? 'bg-[#0A0E14] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex gap-1 justify-between">
            {steps.map(({ step, label, icon: Icon }) => (
              <button
                key={step}
                onClick={() => {
                  setCurrentStep(step);
                  navigate(`/application/${appId}/${step}`);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-medium transition-all whitespace-nowrap ${
                  step === currentStep
                    ? "bg-[#00677F] text-white"
                    : step < currentStep
                    ? "bg-[#739600]/20 text-[#739600]"
                    : "bg-white/5 text-slate-500"
                }`}
                data-testid={`step-nav-${step}`}
              >
                <Icon className="w-3 h-3 shrink-0" />
                <span className="hidden lg:inline">{label}</span>
                <span className="lg:hidden">{step}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Submitted Lock Banner */}
        {isSubmitted && (
          <div className="mb-6 p-4 rounded-xl bg-[#739600]/10 border border-[#739600]/20 flex items-center gap-3" data-testid="submitted-banner">
            <Lock className="w-5 h-5 text-[#739600] flex-shrink-0" />
            <div>
              <p className="text-white font-medium text-sm">Application Submitted</p>
              <p className="text-slate-400 text-xs">Your application has been submitted and is now locked. All fields are read-only.</p>
            </div>
          </div>
        )}
        <div className="mb-8">
          <h1 className={`font-['Montserrat'] text-xl sm:text-2xl lg:text-3xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {steps.find(s => s.step === currentStep)?.label}
          </h1>
          <p className={`${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
            {getCurrentStepLabel() === "Personal Information" && "Tell us about yourself"}
            {getCurrentStepLabel() === "Academic History" && "Share your educational background"}
            {getCurrentStepLabel() === "Employment History" && "Tell us about your work experience"}
            {getCurrentStepLabel() === "Documents Required" && "Upload required documents"}
            {getCurrentStepLabel() === "Official Documents" && "Request official transcripts from your institutions"}
            {getCurrentStepLabel() === "Recommendation" && "Submit your recommendation requests"}
            {getCurrentStepLabel() === "Review & Submit" && "Review and submit your application"}
          </p>
        </div>

        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className={`flex justify-between mt-8 sm:mt-12 pt-6 sm:pt-8 border-t ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
          <Button
            onClick={handleBack}
            disabled={currentStep === 1}
            variant="outline"
            className={`rounded-full px-6 ${theme === 'dark' ? 'border-white/20 text-white hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
            data-testid="prev-step-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {currentStep !== reviewStep ? (
            <Button
              onClick={handleNext}
              disabled={saving}
              className="bg-[#00677F] hover:bg-[#135163] text-white rounded-full px-6"
              data-testid="next-step-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          ) : null}
        </div>
      </div>

      {showChat && <AIChat onClose={() => setShowChat(false)} />}
    </div>
  );
};

export default ApplicationForm;
